import { Component, OnInit } from '@angular/core';
import { PhoneNumber } from 'src/shared/api/api';
import { FormConfig, CustomFormAccessor } from '../form-cva.component';
import {
	NgControl,
	FormBuilder,
	FormControl,
	Validators,
} from '@angular/forms';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import { map, filter, mapTo, takeUntil, shareReplay } from 'rxjs/operators';
import {
	parsePhoneNumber,
	PhoneNumber as Phone,
	parseNumber,
	formatNumber,
	ParsedNumber,
} from 'libphonenumber-js';
import { merge } from 'rxjs';
import { phoneMask } from 'src/shared/constants';

export const phoneNumberFormConfig: FormConfig<PhoneNumber> = {
	id: [''],
	description: [''],
	countryCode: [''],
	areaCode: [''],
	number: ['', [Validators.required]],
	extension: [''],
	isPrimary: [''],
};

@Component({
	selector: 'rpshared-phone-number',
	templateUrl: './phone-number.component.html',
	styleUrls: ['./phone-number.component.css'],
})
export class PhoneNumberComponent extends CustomFormAccessor<PhoneNumber>
	implements OnInit {
	phoneMask: {
		mask: (string | RegExp)[] | false;
	} = { mask: false };
	// Additional Form Controls
	phoneNumberInput = new FormControl('', [
		CustomValidators.isPossiblyValidPhone,
	]);

	validPhoneNumber$ = this.phoneNumberInput.valueChanges.pipe(
		filter(() => this.phoneNumberInput.valid),
		shareReplay(1)
	);

	blankPhoneNumber$ = this.validPhoneNumber$.pipe(
		filter((val) => !val),
		mapTo('')
	);

	internationalPhoneNumber$ = this.validPhoneNumber$.pipe(
		filter((val) => val && val.startsWith('+')),
		map((val) => parsePhoneNumber(val))
	);

	localPhoneNumber$ = this.validPhoneNumber$.pipe(
		filter((val) => val && !val.startsWith('+')),
		map((val) => parsePhoneNumber(val, 'US'))
	);

	phoneNumber$ = merge(
		this.blankPhoneNumber$,
		this.internationalPhoneNumber$,
		this.localPhoneNumber$
	);

	constructor(public ngControl: NgControl, public fb: FormBuilder) {
		super(ngControl, fb, phoneNumberFormConfig);
	}

	ngOnInit() {
		this.phoneNumber$
			.pipe(
				map((val) => this.mapToModel(val)),
				map((phone) => ({ ...this.form.value, ...phone })),
				takeUntil(this.destroyed)
			)
			.subscribe((v) => {
				this.form.setValue(v);
			});
	}

	// model --> view
	writeValue(value: PhoneNumber): void {
		if (value) {
			this.phoneMask = { mask: false };
			this.form.setValue(value, { emitEvent: false });
			this.phoneNumberInput.setValue(this.mapFromModel(value), {
				emitEvent: false,
			});
		} else {
			this.phoneMask = { mask: phoneMask };
		}
	}

	// view --> model
	registerOnChange(fn: (value: PhoneNumber) => void) {
		this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	private mapToModel(input: Phone | string): Partial<PhoneNumber> {
		if (input && this.isPhoneNumber(input)) {
			return <Partial<PhoneNumber>>{
				countryCode: input.countryCallingCode,
				extension: input.ext || '',
				areaCode: input.nationalNumber.slice(0, 3),
				number: input.nationalNumber.slice(3, 10),
			};
		}

		return null;
	}

	private mapFromModel(input: PhoneNumber): string {
		const countryCode = input.countryCode ? `${input.countryCode} ` : '';
		const firstThree = input.number.slice(0, 3);
		const lastFour = input.number.slice(3, 7);
		const extension = input.extension ? ` ext. ${input.extension}` : '';
		return formatNumber(
			parseNumber(
				`${countryCode} (${input.areaCode}) ${firstThree}-${lastFour}${extension}`,
				'US'
			) as ParsedNumber,
			'NATIONAL'
		);
	}

	// Type guards
	private isPhoneNumber(input: Phone | string): input is Phone {
		return (<Phone>input).countryCallingCode !== undefined;
	}
}
